import React, { useEffect, useMemo, useRef, useState } from "react"
import {
  Box,
  Button,
  Card,
  CardMedia,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material"
import { makeStyles } from "@mui/styles"
import PropTypes from "prop-types"
import ColumnsDef from "../../../../table/column"
import noImage from "../../../../../../resources/images/noimage.png"
import dayjs from "dayjs"
import "dayjs/locale/ja"
import "./databable.css"
import {
  ContentPaste as ContentPasteIcon,
  Delete as DeleteIcon,
  OpenInNew as OpenInNewIcon,
  ZoomIn as ZoomInIcon,
  Close as CloseIcon,
} from "@mui/icons-material"
dayjs.locale("ja")

const useStyles = makeStyles({
  root: {
    color: "inherit",
  },
  image: {
    width: "98%",
    maxWidth: "100%",
    boxSizing: "border-box",
  },
  photoCard: {
    width: "98%",
    maxWidth: "100%",
    boxSizing: "border-box",
  },
  previewDialogContent: {
    padding: 0,
    "& img": {
      maxWidth: "100%",
      maxHeight: "85vh",
      objectFit: "contain",
    },
  },
})

const IMAGE_BASE = "https://images-tokyo.s3.ap-northeast-1.amazonaws.com/"

const DialogDetailDetailContentDataTable = (props) => {
  const { scrollContainerRef } = props
  const classes = useStyles()
  const columnDef = useMemo(() => ColumnsDef({}), [])
  const [copyText, setCopyText] = useState("リンクコピー")
  const [diagnosticPdfUrl, setDiagnosticPdfUrl] = useState()
  const [previewPath, setPreviewPath] = useState(null)
  const [tabValue, setTabValue] = useState(0)
  const [hoverPhotoPath, setHoverPhotoPath] = useState(null)
  const savedScrollTop = useRef({ 0: 0, 1: 0 })

  useEffect(() => {
    if (!scrollContainerRef?.current) return
    const top = savedScrollTop.current[tabValue] ?? 0
    scrollContainerRef.current.scrollTop = top
  }, [tabValue, scrollContainerRef])

  const onLinkCopy = (treeId) => {
    navigator.clipboard
      .writeText(
        window.location.host + window.location.pathname + "?tree_id=" + treeId
      )
      .then((res) => {
        setCopyText("コピーしました")
      })
      .catch((e) => {
        setCopyText("コピー失敗")
      })
  }

  const onSelectDiagnosticPdf = (e) => {
    setDiagnosticPdfUrl(e.target.value)
    window.open(e.target.value)
  }

  const photoList = props.data.photos ? props.data.photos.split(",").filter(Boolean) : []

  return (
    <Box
      id="dialog_detail_datatable"
      className={classes.root}
      sx={{ width: "100%", boxSizing: "border-box", ...props.sx }}
    >
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "background.paper",
          py: 0.5,
          mb: 1,
        }}
      >
        <ToggleButtonGroup
          value={tabValue}
          exclusive
          onChange={(e, v) => {
          if (v == null) return
          if (scrollContainerRef?.current) {
            savedScrollTop.current[tabValue] = scrollContainerRef.current.scrollTop
          }
          setTabValue(v)
        }}
          aria-label="表と写真の切り替え"
          fullWidth
        >
          <ToggleButton value={0} aria-controls="detail-tabpanel-table">
            表
          </ToggleButton>
<ToggleButton value={1} aria-controls="detail-tabpanel-photos">
          写真（{photoList.length}枚）
        </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {tabValue === 0 && (
        <Box role="tabpanel" id="detail-tabpanel-table" aria-labelledby="detail-tab-table" sx={{ pt: 1 }}>
          {props.data.diagnostic_pdfs && (
            <Box sx={{ mb: 2 }}>
              <FormControl>
                <InputLabel>診断カルテを表示</InputLabel>
                <Select
                  onChange={onSelectDiagnosticPdf}
                  style={{ width: "200px" }}
                  value={diagnosticPdfUrl}
                >
                  {Object.entries(props.data.diagnostic_pdfs).map(([date, pdf]) => (
                    <MenuItem key={date} value={pdf}>
                      {date === "2021-01-01" && "2021年度"}
                      {date !== "2021-01-01" &&
                        dayjs(date).format("YYYY年MM月DD日")}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          <table>
            {columnDef.flatMap((def) => {
              let d = props.data[def.field]
              if (!d) {
                return []
              }
              if (def.field === "photos") {
                return []
              }
              if (def.field === "diagnostic_pdfs") {
                return []
              }

              switch (def.field) {
                case "date":
                  d = dayjs(d).format("YYYY年M月D日(ddd)")
                  break
                case "height":
                  d = parseFloat(d).toFixed(1)
                  break
                case "is_management_plate":
                case "is_mytree":
                  d = d === 1 ? "有り" : "無し"
                  break
                default:
                  break
              }

              return (
                <tr key={def.field}>
                  <th>{def.headerName}</th>
                  <td>
                    {d}{" "}
                    {def.field === "tree_id" ? (
                      <a
                        href="#"
                        onClick={() => onLinkCopy(d)}
                        style={{ fontSize: "11px", color: "green" }}
                      >
                        {copyText}
                      </a>
                    ) : (
                      ""
                    )}
                  </td>
                </tr>
              )
            })}
          </table>
        </Box>
      )}

      {tabValue === 1 && (
        <Box
          role="tabpanel"
          id="detail-tabpanel-photos"
          aria-labelledby="detail-tab-photos"
          sx={{ pt: 1, maxWidth: "400px", width: "100%", boxSizing: "border-box" }}
        >
          {photoList.length > 0 ? (
            photoList.map((v) => (
              <Card
                key={v}
                className={classes.photoCard}
                variant="outlined"
                sx={{ mt: 1 }}
                onMouseEnter={() => setHoverPhotoPath(v)}
                onMouseLeave={() => setHoverPhotoPath(null)}
              >
                <Box sx={{ position: "relative" }}>
                  <CardMedia
                    component="img"
                    height="200"
                    image={`${IMAGE_BASE}${v}`}
                    alt=""
                    sx={{ objectFit: "contain", cursor: "pointer" }}
                    onClick={() => setPreviewPath(v)}
                  />
                  {hoverPhotoPath === v && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        display: "flex",
                        gap: 2,
                        alignItems: "center",
                        backgroundColor: "rgba(255,255,255,0.9)",
                        borderRadius: 1,
                        px: 1.5,
                        py: 0.5,
                      }}
                    >
                      <IconButton
                        size="small"
                        aria-label="プレビュー"
                        onClick={(e) => {
                          e.stopPropagation()
                          setPreviewPath(v)
                        }}
                        title="画像を拡大表示"
                      >
                        <ZoomInIcon />
                      </IconButton>
                      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                      <IconButton
                        size="small"
                        color="error"
                        aria-label="削除"
                        onClick={(e) => {
                          e.stopPropagation()
                          props.onDeletePhoto(v)
                        }}
                        title="画像を削除"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              </Card>
            ))
          ) : (
            <Box sx={{ py: 2, color: "text.secondary" }}>写真はありません</Box>
          )}
        </Box>
      )}

      {/* 画像プレビューダイアログ（詳細ダイアログの上に表示するため z-index を高く） */}
      <Dialog
        open={!!previewPath}
        onClose={() => setPreviewPath(null)}
        maxWidth={false}
        PaperProps={{
          sx: { maxWidth: "90vw", maxHeight: "90vh", zIndex: 1600 },
        }}
        BackdropProps={{ sx: { zIndex: 1599 } }}
      >
        <DialogContent className={classes.previewDialogContent} sx={{ p: 0 }}>
          {previewPath && (
            <img
              src={`${IMAGE_BASE}${previewPath}`}
              alt="プレビュー"
              style={{ maxWidth: "100%", maxHeight: "85vh", objectFit: "contain" }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "flex-end", px: 2, py: 1 }}>
          <Button startIcon={<CloseIcon />} onClick={() => setPreviewPath(null)}>
            閉じる
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

DialogDetailDetailContentDataTable.propTypes = {
  data: PropTypes.any,
  sx: PropTypes.object,
  onDeletePhoto: PropTypes.func,
  scrollContainerRef: PropTypes.object,
}

export default DialogDetailDetailContentDataTable
